import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Note, NotePage, NoteStats } from '@up2d8/contracts';
import {
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  LoadingState,
  Table,
  Td,
  Textarea,
  Th,
} from '@up2d8/ui';
import { useSession } from '../../shared/session';

/**
 * Demo van de voorbeeldmodule. Laat in één scherm zien wat Stap 0 oplevert:
 * je ziet alleen de notities van je eigen bedrijf, de functie kan per bedrijf uit staan,
 * en de teller wordt bijgewerkt door een eventhandler (dus asynchroon).
 */
export function NotesPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');

  const notes = useQuery({
    queryKey: ['example', 'notes'],
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/v1/example/notes', {
        params: { query: { limit: 25 } },
      });
      if (response.status === 404) return { disabled: true as const };
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return { disabled: false as const, page: data as NotePage };
    },
  });

  const stats = useQuery({
    queryKey: ['example', 'stats'],
    queryFn: async () => {
      const { data, response } = await api.GET('/api/v1/example/stats');
      if (response.status === 404) return null;
      return (data as NoteStats | undefined) ?? null;
    },
    // De teller wordt door de worker bijgewerkt: even later opnieuw kijken.
    refetchInterval: 3000,
  });

  const create = useMutation({
    mutationFn: async (value: string) => {
      const { data, error } = await api.POST('/api/v1/example/notes', { body: { text: value } });
      if (error || !data) throw error ?? new Error('Opslaan mislukt');
      return data as Note;
    },
    onSuccess: async () => {
      setText('');
      await queryClient.invalidateQueries({ queryKey: ['example'] });
    },
  });

  if (notes.isPending) return <LoadingState label={t('common.loading')} rows={4} />;

  if (notes.data?.disabled) {
    return (
      <Card>
        <CardTitle>{t('notes.title')}</CardTitle>
        <p className="mt-2 text-content text-text-secondary">{t('notes.disabled')}</p>
      </Card>
    );
  }

  const items = notes.data?.page.items ?? [];

  return (
    <div className="flex max-w-3xl flex-col gap-section">
      <Card>
        <CardTitle>{t('notes.title')}</CardTitle>
        <p className="mt-2 text-content text-text-secondary">{t('notes.intro')}</p>
        {stats.data ? (
          <p className="mt-element">
            <Badge tone="success">{t('notes.counted', { count: stats.data.notesCreated })}</Badge>
          </p>
        ) : null}
      </Card>

      <Card>
        <form
          className="flex flex-col gap-element"
          onSubmit={(event) => {
            event.preventDefault();
            if (text.trim()) create.mutate(text.trim());
          }}
        >
          <Textarea
            label={t('notes.newLabel')}
            placeholder={t('notes.newPlaceholder')}
            value={text}
            onChange={(event) => setText(event.target.value)}
            maxLength={500}
            error={create.isError ? t('errors.generic') : undefined}
          />
          <div className="flex justify-end">
            <Button type="submit" loading={create.isPending} disabled={!text.trim()}>
              {t('notes.create')}
            </Button>
          </div>
        </form>
      </Card>

      <Card>
        {items.length === 0 ? (
          <EmptyState title={t('notes.empty')} description={t('notes.emptyHint')} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('notes.newLabel')}</Th>
                <Th className="w-40">{t('notes.createdAt')}</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((note) => (
                <tr key={note.id}>
                  <Td className="text-text-primary">{note.text}</Td>
                  <Td>{new Date(note.createdAt).toLocaleString()}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
